/**
 * Encoder validation and error handling utilities
 * Provides detailed error messages indicating which settings need to be changed
 */

import type { TFunction } from "i18next";
import type {
  
  AudioCodec,
  
  HardwareAcceleration,
} from "../../../../entities/webcodecs/config";

// ============================================
// TYPES
// ============================================

export interface VideoValidationConfig {
  codecProfile: string;
  width: number;
  height: number;
  bitrate: number;
  framerate: number;
  hardwareAcceleration: HardwareAcceleration;
}

export interface AudioValidationConfig {
  audioCodec: AudioCodec;
  audioProfile: string;
  audioSampleRate: number;
  audioChannels: number;
  audioBitrate: number;
}

export interface ValidationError {
  type: "video" | "audio";
  field: string;
  message: string;
  suggestion: string;
  currentValue: string | number;
}

export interface ValidationResult {
  success: boolean;
  errors: ValidationError[];
  message: string;
}

// ============================================
// VIDEO VALIDATION
// ============================================

/**
 * Validates video encoder configuration and returns detailed errors
 */
export async function validateVideoConfig(
  config: VideoValidationConfig,
  t: TFunction
): Promise<ValidationResult> {
  const errors: ValidationError[] = [];

  try {
    const videoConfig: VideoEncoderConfig = {
      codec: config.codecProfile,
      width: config.width,
      height: config.height,
      bitrate: config.bitrate,
      framerate: config.framerate,
      hardwareAcceleration: config.hardwareAcceleration,
    };

    const result = await VideoEncoder.isConfigSupported(videoConfig);

    if (!result.supported) {
      // Try to determine which specific setting is causing the issue
      const diagnostics = await diagnoseVideoIssue(config);

      if (diagnostics.profileIssue) {
        errors.push({
          type: "video",
          field: "codecProfile",
          message: t("SETTINGS.FORMS.ENCODERS.ERRORS.profileNotSupported", {
            profile: config.codecProfile,
          }),
          suggestion: t("SETTINGS.FORMS.ENCODERS.ERRORS.tryDifferentProfile"),
          currentValue: config.codecProfile,
        });
      }

      if (diagnostics.hwAccelIssue) {
        errors.push({
          type: "video",
          field: "hardwareAcceleration",
          message: t("SETTINGS.FORMS.ENCODERS.ERRORS.hwAccelNotSupported", {
            mode: config.hardwareAcceleration,
          }),
          suggestion: t("SETTINGS.FORMS.ENCODERS.ERRORS.tryDifferentHwAccel"),
          currentValue: config.hardwareAcceleration,
        });
      }

      if (diagnostics.resolutionIssue) {
        errors.push({
          type: "video",
          field: "resolution",
          message: t("SETTINGS.FORMS.ENCODERS.ERRORS.resolutionNotSupported", {
            width: config.width,
            height: config.height,
          }),
          suggestion: t("SETTINGS.FORMS.ENCODERS.ERRORS.tryLowerResolution"),
          currentValue: `${config.width}x${config.height}`,
        });
      }

      if (diagnostics.bitrateIssue) {
        errors.push({
          type: "video",
          field: "bitrate",
          message: t("SETTINGS.FORMS.ENCODERS.ERRORS.bitrateNotSupported", {
            bitrate: config.bitrate / 1000,
          }),
          suggestion: t("SETTINGS.FORMS.ENCODERS.ERRORS.adjustBitrate"),
          currentValue: config.bitrate,
        });
      }

      // If no specific issue found, give general guidance
      if (errors.length === 0) {
        errors.push({
          type: "video",
          field: "general",
          message: t("SETTINGS.FORMS.ENCODERS.ERRORS.videoConfigNotSupported"),
          suggestion: t("SETTINGS.FORMS.ENCODERS.ERRORS.tryDifferentVideoSettings"),
          currentValue: config.codecProfile,
        });
      }
    }
  } catch (e) {
    console.error("Video validation error:", e);
    errors.push({
      type: "video",
      field: "general",
      message: t("SETTINGS.FORMS.ENCODERS.ERRORS.videoValidationFailed"),
      suggestion: t("SETTINGS.FORMS.ENCODERS.ERRORS.checkVideoSettings"),
      currentValue: config.codecProfile,
    });
  }

  return {
    success: errors.length === 0,
    errors,
    message: formatValidationMessage(errors, t),
  };
}

/**
 * Diagnose which specific video setting is causing the issue
 */
async function diagnoseVideoIssue(config: VideoValidationConfig): Promise<{
  profileIssue: boolean;
  hwAccelIssue: boolean;
  resolutionIssue: boolean;
  bitrateIssue: boolean;
}> {
  const result = {
    profileIssue: false,
    hwAccelIssue: false,
    resolutionIssue: false,
    bitrateIssue: false,
  };

  // Test with software encoding to isolate HW accel issues
  try {
    const swConfig: VideoEncoderConfig = {
      codec: config.codecProfile,
      width: config.width,
      height: config.height,
      bitrate: config.bitrate,
      framerate: config.framerate,
      hardwareAcceleration: "prefer-software",
    };
    const swResult = await VideoEncoder.isConfigSupported(swConfig);
    if (swResult.supported && config.hardwareAcceleration !== "prefer-software") {
      result.hwAccelIssue = true;
      return result;
    }
  } catch {
    // Continue diagnosis
  }

  // Test with lower resolution to isolate resolution issues
  try {
    const lowResConfig: VideoEncoderConfig = {
      codec: config.codecProfile,
      width: 1280,
      height: 720,
      bitrate: config.bitrate,
      framerate: config.framerate,
      hardwareAcceleration: config.hardwareAcceleration,
    };
    const lowResResult = await VideoEncoder.isConfigSupported(lowResConfig);
    if (lowResResult.supported && (config.width > 1280 || config.height > 720)) {
      result.resolutionIssue = true;
      return result;
    }
  } catch {
    // Continue diagnosis
  }

  // Test with different bitrate
  try {
    const lowBitrateConfig: VideoEncoderConfig = {
      codec: config.codecProfile,
      width: config.width,
      height: config.height,
      bitrate: 2000000, // 2 Mbps
      framerate: config.framerate,
      hardwareAcceleration: config.hardwareAcceleration,
    };
    const lowBitrateResult = await VideoEncoder.isConfigSupported(lowBitrateConfig);
    if (lowBitrateResult.supported && config.bitrate > 2000000) {
      result.bitrateIssue = true;
      return result;
    }
  } catch {
    // Continue diagnosis
  }

  // If nothing else, likely a profile issue
  result.profileIssue = true;
  return result;
}

// ============================================
// AUDIO VALIDATION
// ============================================

/**
 * Validates audio encoder configuration and returns detailed errors
 */
export async function validateAudioConfig(
  config: AudioValidationConfig,
  t: TFunction
): Promise<ValidationResult> {
  const errors: ValidationError[] = [];

  try {
    const audioCodecString = config.audioProfile || config.audioCodec;
    const audioConfig: AudioEncoderConfig = {
      codec: audioCodecString,
      sampleRate: config.audioSampleRate,
      numberOfChannels: config.audioChannels,
      bitrate: config.audioBitrate,
    };

    const result = await AudioEncoder.isConfigSupported(audioConfig);

    if (!result.supported) {
      // Try to determine which specific setting is causing the issue
      const diagnostics = await diagnoseAudioIssue(config);

      if (diagnostics.codecIssue) {
        errors.push({
          type: "audio",
          field: "audioCodec",
          message: t("SETTINGS.FORMS.ENCODERS.ERRORS.audioCodecNotSupported", {
            codec: config.audioCodec.toUpperCase(),
          }),
          suggestion: t("SETTINGS.FORMS.ENCODERS.ERRORS.tryDifferentAudioCodec"),
          currentValue: config.audioCodec,
        });
      }

      if (diagnostics.sampleRateIssue) {
        errors.push({
          type: "audio",
          field: "audioSampleRate",
          message: t("SETTINGS.FORMS.ENCODERS.ERRORS.sampleRateNotSupported", {
            rate: config.audioSampleRate / 1000,
          }),
          suggestion: t("SETTINGS.FORMS.ENCODERS.ERRORS.tryDifferentSampleRate"),
          currentValue: config.audioSampleRate,
        });
      }

      if (diagnostics.channelsIssue) {
        errors.push({
          type: "audio",
          field: "audioChannels",
          message: t("SETTINGS.FORMS.ENCODERS.ERRORS.channelsNotSupported", {
            channels: config.audioChannels === 1 ? "Mono" : "Stereo",
          }),
          suggestion: t("SETTINGS.FORMS.ENCODERS.ERRORS.tryDifferentChannels"),
          currentValue: config.audioChannels,
        });
      }

      if (diagnostics.bitrateIssue) {
        errors.push({
          type: "audio",
          field: "audioBitrate",
          message: t("SETTINGS.FORMS.ENCODERS.ERRORS.audioBitrateNotSupported", {
            bitrate: config.audioBitrate / 1000,
          }),
          suggestion: t("SETTINGS.FORMS.ENCODERS.ERRORS.tryDifferentAudioBitrate"),
          currentValue: config.audioBitrate,
        });
      }

      // If no specific issue found, give general guidance
      if (errors.length === 0) {
        errors.push({
          type: "audio",
          field: "general",
          message: t("SETTINGS.FORMS.ENCODERS.ERRORS.audioConfigNotSupported"),
          suggestion: t("SETTINGS.FORMS.ENCODERS.ERRORS.tryDifferentAudioSettings"),
          currentValue: config.audioCodec,
        });
      }
    }
  } catch (e) {
    console.error("Audio validation error:", e);
    errors.push({
      type: "audio",
      field: "general",
      message: t("SETTINGS.FORMS.ENCODERS.ERRORS.audioValidationFailed"),
      suggestion: t("SETTINGS.FORMS.ENCODERS.ERRORS.checkAudioSettings"),
      currentValue: config.audioCodec,
    });
  }

  return {
    success: errors.length === 0,
    errors,
    message: formatValidationMessage(errors, t),
  };
}

/**
 * Diagnose which specific audio setting is causing the issue
 * Order of diagnosis: bitrate -> sampleRate -> channels -> codec
 * This order is based on probability of issues in real-world scenarios
 */
async function diagnoseAudioIssue(config: AudioValidationConfig): Promise<{
  codecIssue: boolean;
  sampleRateIssue: boolean;
  channelsIssue: boolean;
  bitrateIssue: boolean;
}> {
  const result = {
    codecIssue: false,
    sampleRateIssue: false,
    channelsIssue: false,
    bitrateIssue: false,
  };

  // Use the proper codec string (AAC = mp4a.40.2, not "aac")
  const audioCodecString =
    config.audioProfile || (config.audioCodec === "aac" ? "mp4a.40.2" : config.audioCodec);

  console.log("[Audio Diagnosis] Testing config:", {
    codec: audioCodecString,
    sampleRate: config.audioSampleRate,
    channels: config.audioChannels,
    bitrate: config.audioBitrate,
  });

  // 1. TEST BITRATE FIRST with a known-good config (128kbps, 48kHz, stereo)
  // This is the most common issue
  try {
    const lowBitrateConfig: AudioEncoderConfig = {
      codec: audioCodecString,
      sampleRate: 48000,
      numberOfChannels: 2,
      bitrate: 128000,
    };
    const lowBitrateResult = await AudioEncoder.isConfigSupported(lowBitrateConfig);
    console.log("[Audio Diagnosis] 128kbps test:", lowBitrateResult.supported);

    if (lowBitrateResult.supported) {
      // The base config works with 128kbps. Now test if the issue is the bitrate specifically.
      const currentBitrateConfig: AudioEncoderConfig = {
        codec: audioCodecString,
        sampleRate: 48000,
        numberOfChannels: 2,
        bitrate: config.audioBitrate,
      };
      const currentBitrateResult = await AudioEncoder.isConfigSupported(currentBitrateConfig);
      console.log("[Audio Diagnosis] Current bitrate test:", currentBitrateResult.supported);

      if (!currentBitrateResult.supported) {
        // The issue is definitely the bitrate
        result.bitrateIssue = true;
        console.log("[Audio Diagnosis] Diagnosed: BITRATE issue");
        return result;
      }
    }
  } catch (e) {
    console.log("[Audio Diagnosis] Bitrate test error:", e);
  }

  // 2. TEST SAMPLE RATE - use safe bitrate (128kbps)
  try {
    if (config.audioSampleRate !== 48000) {
      const sr48Config: AudioEncoderConfig = {
        codec: audioCodecString,
        sampleRate: 48000,
        numberOfChannels: config.audioChannels,
        bitrate: 128000, // Use safe bitrate
      };
      const currentSrConfig: AudioEncoderConfig = {
        codec: audioCodecString,
        sampleRate: config.audioSampleRate,
        numberOfChannels: config.audioChannels,
        bitrate: 128000, // Use safe bitrate
      };

      const sr48Result = await AudioEncoder.isConfigSupported(sr48Config);
      const currentSrResult = await AudioEncoder.isConfigSupported(currentSrConfig);

      if (sr48Result.supported && !currentSrResult.supported) {
        result.sampleRateIssue = true;
        console.log("[Audio Diagnosis] Diagnosed: SAMPLE RATE issue");
        return result;
      }
    }
  } catch (e) {
    console.log("[Audio Diagnosis] Sample rate test error:", e);
  }

  // 3. TEST CHANNELS - use safe bitrate (128kbps) and sample rate (48kHz)
  try {
    if (config.audioChannels !== 2) {
      const stereoConfig: AudioEncoderConfig = {
        codec: audioCodecString,
        sampleRate: 48000,
        numberOfChannels: 2,
        bitrate: 128000,
      };
      const currentChannelConfig: AudioEncoderConfig = {
        codec: audioCodecString,
        sampleRate: 48000,
        numberOfChannels: config.audioChannels,
        bitrate: 128000,
      };

      const stereoResult = await AudioEncoder.isConfigSupported(stereoConfig);
      const currentChannelResult = await AudioEncoder.isConfigSupported(currentChannelConfig);

      if (stereoResult.supported && !currentChannelResult.supported) {
        result.channelsIssue = true;
        console.log("[Audio Diagnosis] Diagnosed: CHANNELS issue");
        return result;
      }
    }
  } catch (e) {
    console.log("[Audio Diagnosis] Channels test error:", e);
  }

  // 4. TEST CODEC - only if nothing else worked, test with alternate codec
  try {
    // Test if the alternate codec works with the same "safe" settings
    const altCodec = config.audioCodec === "opus" ? "mp4a.40.2" : "opus";
    const altSampleRate = config.audioCodec === "opus" ? 48000 : 48000; // Opus requires 48kHz

    const altCodecConfig: AudioEncoderConfig = {
      codec: altCodec,
      sampleRate: altSampleRate,
      numberOfChannels: 2,
      bitrate: 128000,
    };

    // Also test the current codec with safe settings
    const currentCodecConfig: AudioEncoderConfig = {
      codec: audioCodecString,
      sampleRate: 48000,
      numberOfChannels: 2,
      bitrate: 128000,
    };

    const altCodecResult = await AudioEncoder.isConfigSupported(altCodecConfig);
    const currentCodecResult = await AudioEncoder.isConfigSupported(currentCodecConfig);

    console.log("[Audio Diagnosis] Current codec (safe config):", currentCodecResult.supported);
    console.log("[Audio Diagnosis] Alt codec (safe config):", altCodecResult.supported);

    if (!currentCodecResult.supported && altCodecResult.supported) {
      result.codecIssue = true;
      console.log("[Audio Diagnosis] Diagnosed: CODEC issue");
      return result;
    }
  } catch (e) {
    console.log("[Audio Diagnosis] Codec test error:", e);
  }

  // If we get here with the safe config not working, it's likely a codec issue
  // or something fundamentally wrong
  console.log("[Audio Diagnosis] Could not determine specific issue, defaulting to codec issue");
  result.codecIssue = true;
  return result;
}

// ============================================
// HELPERS
// ============================================

/**
 * Format validation errors into a user-friendly message
 */
function formatValidationMessage(errors: ValidationError[], _t: TFunction): string {
  if (errors.length === 0) {
    return "";
  }

  const parts: string[] = [];

  for (const error of errors) {
    parts.push(`${error.message} ${error.suggestion}`);
  }

  return parts.join("\n");
}

/**
 * Get field-specific guidance for fixing validation errors
 */
export function getFieldGuidance(field: string, t: TFunction): string {
  const guidanceMap: Record<string, string> = {
    codecProfile: t("SETTINGS.FORMS.ENCODERS.ERRORS.GUIDANCE.profile"),
    hardwareAcceleration: t("SETTINGS.FORMS.ENCODERS.ERRORS.GUIDANCE.hwAccel"),
    resolution: t("SETTINGS.FORMS.ENCODERS.ERRORS.GUIDANCE.resolution"),
    bitrate: t("SETTINGS.FORMS.ENCODERS.ERRORS.GUIDANCE.bitrate"),
    audioCodec: t("SETTINGS.FORMS.ENCODERS.ERRORS.GUIDANCE.audioCodec"),
    audioSampleRate: t("SETTINGS.FORMS.ENCODERS.ERRORS.GUIDANCE.sampleRate"),
    audioChannels: t("SETTINGS.FORMS.ENCODERS.ERRORS.GUIDANCE.channels"),
    audioBitrate: t("SETTINGS.FORMS.ENCODERS.ERRORS.GUIDANCE.audioBitrate"),
  };

  return guidanceMap[field] || "";
}

/**
 * Combined validation for both video and audio
 */
export async function validateEncodersConfig(
  videoConfig: VideoValidationConfig,
  audioConfig: AudioValidationConfig,
  t: TFunction
): Promise<{ video: ValidationResult; audio: ValidationResult }> {
  const [videoResult, audioResult] = await Promise.all([
    validateVideoConfig(videoConfig, t),
    validateAudioConfig(audioConfig, t),
  ]);

  return { video: videoResult, audio: audioResult };
}
